import { useEffect } from "react";

// Sets document.title, the meta description tag, and the Open Graph tags
// for whichever page renders this. Previously every route shared the one
// hardcoded set from index.html — real per-page values here benefit real
// users (correct tab titles/bookmarks), crawlers (each route's pre-rendered
// snapshot picks these up too, since PageMeta runs before the snapshot is
// captured), AND social link previews (WhatsApp/Facebook/Slack/iMessage
// unfurls read og:title/og:description/og:image, a completely separate tag
// set from <title>/meta description — fixing one does not fix the other,
// found this the hard way when a shared property link still showed the
// generic site-wide preview after the <title> fix already shipped).
const DEFAULT_TITLE = "GuestLinkup - Himalayan Homestays";
const DEFAULT_DESCRIPTION =
  "GuestLinkup connects travelers with authentic family-run homestays in Northeast India.";
const DEFAULT_OG_IMAGE = "https://www.guestlinkup.com/images/og-image.png";
const DEFAULT_OG_URL = "https://www.guestlinkup.com/";

const setMetaContent = (selector, content) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", content);
};

const setCanonical = (href) => {
  const link = document.querySelector('link[rel="canonical"]');
  if (link) link.setAttribute("href", href);
};

// The site's one true URL form — no trailing slash (except the root),
// https, www — matching generate-sitemap.js's convention and every
// internal <Link>. Without this, the canonical tag echoed back whichever
// exact variant a crawler happened to request (http/https, www/non-www,
// trailing slash or not), so a page reached via a different variant than
// the sitemap's would declare ITSELF canonical instead of agreeing with
// the sitemap — real duplicate-content signal, which is exactly what
// produced GSC's "Google chose different canonical than user" flag on
// /property/<id>/ pages.
const normalizeUrl = (href) => {
  try {
    const u = new URL(href);
    let pathname = u.pathname;
    if (pathname.length > 1 && pathname.endsWith("/")) {
      pathname = pathname.slice(0, -1);
    }
    return `https://www.guestlinkup.com${pathname}`;
  } catch {
    return DEFAULT_OG_URL;
  }
};

const PageMeta = ({ title, description, image, url }) => {
  useEffect(() => {
    const finalTitle = title || DEFAULT_TITLE;
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const finalImage = image || DEFAULT_OG_IMAGE;
    const finalUrl = normalizeUrl(
      url ||
        (typeof window !== "undefined" ? window.location.href : DEFAULT_OG_URL)
    );

    document.title = finalTitle;
    setMetaContent('meta[name="description"]', finalDescription);
    setMetaContent('meta[property="og:title"]', finalTitle);
    setMetaContent('meta[property="og:description"]', finalDescription);
    setMetaContent('meta[property="og:image"]', finalImage);
    setMetaContent('meta[property="og:url"]', finalUrl);
    setCanonical(finalUrl);

    // Reset to the site-wide defaults on unmount so navigating away (e.g.
    // via browser back button, mid-transition) never leaves a stale title
    // from a page that's no longer showing.
    return () => {
      document.title = DEFAULT_TITLE;
      setMetaContent('meta[name="description"]', DEFAULT_DESCRIPTION);
      setMetaContent('meta[property="og:title"]', DEFAULT_TITLE);
      setMetaContent('meta[property="og:description"]', DEFAULT_DESCRIPTION);
      setMetaContent('meta[property="og:image"]', DEFAULT_OG_IMAGE);
      setMetaContent('meta[property="og:url"]', DEFAULT_OG_URL);
      setCanonical(DEFAULT_OG_URL);
    };
  }, [title, description, image, url]);

  return null;
};

export default PageMeta;
